import * as Sentry from '@sentry/react';
import { userIdentityChanged, sessionRecovered } from 'auth/identityActions';
import type { Action } from 'redux';
import type { Task } from 'redux-saga';
import {
  call,
  fork,
  cancel,
  take,
  delay,
  put,
  race,
  takeEvery,
  takeLatest,
  throttle,
} from 'redux-saga/effects';
import { connectionVerified, setOnline } from 'store/connection/actions';
import {
  ARCHIVE_STORAGE_UNIT,
  CREATE_STORAGE_UNIT_REQUEST,
  DELETE_ALL_ARCHIVED_STORAGE_UNIT,
  GET_ACCESS_INFORMATION_FOR_STORAGE_UNIT_REQUEST,
  GET_APPROVED_FILES_REQUEST,
  GET_ARCHIVED_REQUEST,
  GET_RECENT_STORAGE_UNITS_REQUEST,
  GET_STORAGE_UNIT_REQUEST,
  GET_STORAGE_UNITS_REQUEST,
  GET_TEMPLATES_REQUEST,
  GRANT_ACCESS_REQUEST,
  LEASE_ON_STORAGE_UNIT_REQUEST,
  POLL_STORAGE_UNIT_LEASES_REQUEST,
  REMOVE_ACCESS_REQUEST,
  REMOVE_STORAGE_UNIT_REQUEST,
  RESTORE_STORAGE_UNIT,
  StorageUnitType,
  UPDATE_STORAGE_UNIT_REQUEST,
  UpdateActionOrigin,
} from './types';
import {
  getAccessInformationForStorageUnit,
  grantAccessToStorageUnit,
  removeAccessToStorageUnit,
} from './accessSagas';
import { getStorageUnits } from './browsingSagas';
import {
  fetchApprovedCollection,
  fetchArchivedCollection,
  fetchRecentCollection,
  fetchTemplatesCollection,
} from './collectionSagas';
import { createStorageUnit } from './createSagas';
import {
  archiveStorageUnit,
  deleteAllArchivedStorageUnits,
  removeStorageUnit,
  restoreStorageUnit,
} from './lifecycleSagas';
import { leaseOnStorageUnit, pollStorageUnitLeases } from './leaseSagas';
import { handleReconnectSync } from './offlineSyncSagas';
import { getStorageUnit } from './openSagas';
import { designerClosing, generateMissingThumbnail } from './reducer';
import {
  getStorageUnitsRequest,
  updateStorageUnitRequest,
} from './sagaActions';
import type { GetStorageUnitsRequestActionInterface } from './sagaActions';
import { updateStorageUnit } from './updateSagas';

const loadThumbnailGenerator = () =>
  import('util/thumbnailGenerator').then(
    (module) => module.generateThumbnailsFromDiagram,
  );

const VERIFIED_SYNC_THROTTLE_MS = 30000;

function* handleGenerateMissingThumbnail(action: {
  type: string;
  payload: string;
}) {
  const fileId = action.payload;
  const isClosingThisFile = (closingAction: Action) =>
    designerClosing.match(closingAction) && closingAction.payload === fileId;

  let loaded: {
    generator?: Awaited<ReturnType<typeof loadThumbnailGenerator>>;
  };
  try {
    loaded = yield race({
      generator: call(loadThumbnailGenerator),
      closing: take(isClosingThisFile),
    });
  } catch (error) {
    Sentry.captureException(error, {
      tags: { saga: 'generateMissingThumbnail' },
      level: 'warning',
    });
    return;
  }
  const generateThumbnailsFromDiagram = loaded.generator;
  if (!generateThumbnailsFromDiagram) {
    return;
  }

  yield race({
    rendered: delay(2000),
    closing: take(isClosingThisFile),
  });

  const thumbnails = generateThumbnailsFromDiagram();

  if (thumbnails) {
    yield put(
      updateStorageUnitRequest(
        {
          id: fileId,
          type: StorageUnitType.File,
          ...thumbnails,
        },
        UpdateActionOrigin.THUMBNAIL,
      ),
    );
  }
}

function* watchStorageUnitLists() {
  let currentList: Task | undefined;
  while (true) {
    const action: GetStorageUnitsRequestActionInterface = yield take(
      GET_STORAGE_UNITS_REQUEST,
    );
    if (action.payload.descendantId) {
      yield fork(getStorageUnits, action);
      continue;
    }
    if (currentList) {
      yield cancel(currentList);
    }
    currentList = yield fork(getStorageUnits, action);
  }
}

function* watchCloudsave() {
  yield takeEvery(CREATE_STORAGE_UNIT_REQUEST, createStorageUnit);
  yield fork(watchStorageUnitLists);
  yield takeEvery(GET_STORAGE_UNIT_REQUEST, getStorageUnit);
  yield takeEvery(UPDATE_STORAGE_UNIT_REQUEST, updateStorageUnit);
  yield takeEvery(REMOVE_STORAGE_UNIT_REQUEST, removeStorageUnit);
  yield takeEvery(ARCHIVE_STORAGE_UNIT, archiveStorageUnit);
  yield takeEvery(RESTORE_STORAGE_UNIT, restoreStorageUnit);
  yield takeEvery(
    DELETE_ALL_ARCHIVED_STORAGE_UNIT,
    deleteAllArchivedStorageUnits,
  );
  yield takeEvery(GRANT_ACCESS_REQUEST, grantAccessToStorageUnit);
  yield takeEvery(
    GET_ACCESS_INFORMATION_FOR_STORAGE_UNIT_REQUEST,
    getAccessInformationForStorageUnit,
  );
  yield takeEvery(REMOVE_ACCESS_REQUEST, removeAccessToStorageUnit);
  yield takeEvery(LEASE_ON_STORAGE_UNIT_REQUEST, leaseOnStorageUnit);
  yield takeLatest(POLL_STORAGE_UNIT_LEASES_REQUEST, pollStorageUnitLeases);
  yield takeEvery(GET_TEMPLATES_REQUEST, fetchTemplatesCollection);
  yield takeEvery(GET_ARCHIVED_REQUEST, fetchArchivedCollection);
  yield takeEvery(GET_APPROVED_FILES_REQUEST, fetchApprovedCollection);
  yield takeLatest(GET_RECENT_STORAGE_UNITS_REQUEST, fetchRecentCollection);
  yield takeEvery(
    generateMissingThumbnail.type,
    handleGenerateMissingThumbnail,
  );
  yield takeEvery(setOnline.type, handleReconnectSync);
  yield takeEvery(sessionRecovered.type, handleReconnectSync);
  // Saves queued during a brief blip that never went offline still need a
  // retry; throttled so a service that keeps failing is not hammered.
  yield throttle(
    VERIFIED_SYNC_THROTTLE_MS,
    connectionVerified.type,
    handleReconnectSync,
  );
}

// Cancelling the watcher also cancels its in-flight saga tasks, so an old
// account's folder context cannot continue under a new identity.
export function* cloudsaveSagas() {
  let task: Task = yield fork(watchCloudsave);
  while (true) {
    const action: ReturnType<typeof userIdentityChanged> = yield take(
      userIdentityChanged.type,
    );
    yield cancel(task);
    task = yield fork(watchCloudsave);
    if (action.payload) yield put(getStorageUnitsRequest({}));
  }
}
